import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Download, Trash2, ShieldCheck, Heart, ScrollText } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

export default function SettingsPage() {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState(user?.name || "");
  const [bio, setBio] = useState(user?.bio || "");
  const [avatarUrl, setAvatarUrl] = useState(user?.avatar_url || "");
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteText, setDeleteText] = useState("");
  const [deleting, setDeleting] = useState(false);

  const exportData = async () => {
    try {
      const { data } = await api.get("/users/me/data-export");
      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `lazcoach-data-${user?.email?.replace(/[^a-z0-9]/gi, "_")}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Your data has been exported.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const deleteAccount = async () => {
    if (deleteText !== "DELETE") {
      toast.error("Type DELETE to confirm.");
      return;
    }
    setDeleting(true);
    try {
      await api.delete("/users/me");
      toast.success("Account deleted. Take care.");
      setConfirmDelete(false);
      setUser(false);
      navigate("/", { replace: true });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setDeleting(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.patch("/users/me", {
        name,
        bio,
        avatar_url: avatarUrl || null,
      });
      setUser(data);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const initials = (user?.name || "?")
    .split(" ")
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div className="max-w-2xl mx-auto space-y-8">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-amber-700">Account</div>
        <h1 className="font-serif text-4xl text-stone-900 tracking-tight mt-1">Settings</h1>
      </div>

      <form
        onSubmit={save}
        className="rounded-3xl bg-stone-50 border border-stone-200 p-6 sm:p-8 space-y-5"
      >
        <div className="flex items-center gap-5">
          <Avatar className="h-20 w-20">
            <AvatarImage src={avatarUrl || undefined} />
            <AvatarFallback className="bg-amber-700 text-stone-50 text-xl">
              {initials}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1">
            <div className="font-serif text-xl text-stone-900">{user?.name}</div>
            <div className="text-sm text-stone-500">{user?.email}</div>
            <Badge className="mt-2 rounded-full bg-stone-100 text-stone-700 text-[10px]">
              {user?.role === "admin" ? "Administrator" : "Client"}
            </Badge>
          </div>
        </div>

        <div>
          <Label className="text-stone-700">Name</Label>
          <Input
            data-testid="settings-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="mt-2 rounded-xl border-stone-200 bg-white focus-visible:ring-amber-700 focus-visible:border-amber-700"
          />
        </div>
        <div>
          <Label className="text-stone-700">Bio</Label>
          <Textarea
            data-testid="settings-bio-input"
            rows={4}
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            className="mt-2 rounded-2xl border-stone-200 bg-white focus-visible:ring-amber-700 focus-visible:border-amber-700"
          />
        </div>
        <div>
          <Label className="text-stone-700">Avatar URL</Label>
          <Input
            data-testid="settings-avatar-input"
            value={avatarUrl}
            onChange={(e) => setAvatarUrl(e.target.value)}
            placeholder="https://..."
            className="mt-2 rounded-xl border-stone-200 bg-white focus-visible:ring-amber-700 focus-visible:border-amber-700"
          />
        </div>
        <div className="flex justify-end">
          <Button
            type="submit"
            data-testid="settings-save-button"
            disabled={saving}
            className="rounded-full bg-amber-700 hover:bg-amber-800 text-stone-50"
          >
            {saving ? "Saving..." : "Save changes"}
          </Button>
        </div>
      </form>

      <div className="rounded-3xl bg-stone-50 border border-stone-200 p-6 sm:p-8">
        <div className="flex items-center gap-2 mb-4">
          <ShieldCheck size={18} strokeWidth={1.5} className="text-green-700" />
          <div className="font-serif text-2xl text-stone-900">
            Privacy & data rights
          </div>
        </div>
        <p className="text-sm text-stone-600 mb-5">
          Your rights under <span className="font-medium">PIPEDA + Québec Law 25</span>. Download a full copy of your data at any time, or permanently close your account.
        </p>
        <div className="flex flex-wrap gap-3">
          <Button
            data-testid="export-data-button"
            onClick={exportData}
            variant="outline"
            className="rounded-full border-stone-300 text-stone-700 hover:bg-stone-100"
          >
            <Download size={14} strokeWidth={1.5} className="mr-2" /> Export my data (JSON)
          </Button>
          <Button
            data-testid="delete-account-button"
            onClick={() => setConfirmDelete(true)}
            variant="outline"
            className="rounded-full border-red-300 text-red-700 hover:bg-red-50"
          >
            <Trash2 size={14} strokeWidth={1.5} className="mr-2" /> Delete my account
          </Button>
        </div>
        <div className="mt-6 grid sm:grid-cols-3 gap-3">
          <Link
            to="/legal/terms"
            data-testid="link-legal-nda"
            className="rounded-2xl border border-stone-200 bg-white p-4 hover:bg-stone-100 transition-all"
          >
            <ScrollText size={16} strokeWidth={1.5} className="text-amber-700" />
            <div className="font-serif text-stone-900 mt-2">Coaching terms</div>
            <div className="text-xs text-stone-500 mt-1">Client agreement & policies</div>
          </Link>
          <Link
            to="/legal/privacy"
            data-testid="link-legal-privacy"
            className="rounded-2xl border border-stone-200 bg-white p-4 hover:bg-stone-100 transition-all"
          >
            <ShieldCheck size={16} strokeWidth={1.5} className="text-green-700" />
            <div className="font-serif text-stone-900 mt-2">Privacy policy</div>
            <div className="text-xs text-stone-500 mt-1">PIPEDA · Québec Law 25</div>
          </Link>
          <Link
            to="/legal/ethics"
            data-testid="link-legal-ethics"
            className="rounded-2xl border border-stone-200 bg-white p-4 hover:bg-stone-100 transition-all"
          >
            <Heart size={16} strokeWidth={1.5} className="text-amber-700" />
            <div className="font-serif text-stone-900 mt-2">Code of ethics</div>
            <div className="text-xs text-stone-500 mt-1">Humanitarian charter</div>
          </Link>
        </div>
      </div>

      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent className="rounded-3xl border-stone-200 bg-stone-50">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-stone-900">
              Permanently delete your account?
            </DialogTitle>
            <DialogDescription className="text-stone-500">
              Your personal data (profile, goals, tasks, application) will be erased within 30 days. Financial booking and contract records will be anonymised and retained for 7 years as required by Canadian tax law.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label className="text-stone-700 text-sm">Type DELETE to confirm</Label>
            <Input
              data-testid="delete-confirm-input"
              value={deleteText}
              onChange={(e) => setDeleteText(e.target.value)}
              placeholder="DELETE"
              className="rounded-xl border-stone-200 bg-white focus-visible:ring-red-700 focus-visible:border-red-700"
            />
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setConfirmDelete(false)}
              className="rounded-full text-stone-600"
            >
              Cancel
            </Button>
            <Button
              data-testid="delete-confirm-button"
              onClick={deleteAccount}
              disabled={deleting || deleteText !== "DELETE"}
              className="rounded-full bg-red-700 hover:bg-red-800 text-stone-50 disabled:opacity-50"
            >
              Delete forever
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="rounded-3xl bg-stone-50 border border-stone-200 p-6 sm:p-8">
        <div className="font-serif text-xl text-stone-900">Sign out</div>
        <p className="text-sm text-stone-500 mt-1">Take a breath. Come back when ready.</p>
        <Button
          data-testid="settings-logout-button"
          onClick={logout}
          variant="outline"
          className="mt-4 rounded-full border-stone-300 text-stone-700 hover:bg-stone-100"
        >
          Sign out
        </Button>
      </div>
    </div>
  );
}
